import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Trophy, 
  Medal, 
  Award, 
  Users, 
  TrendingUp, 
  Clock, 
  Flame,
  Target,
  Calendar,
  Loader2,
  Crown,
  Star
} from "lucide-react";

export default function LeaderboardPage() {
  const [loading, setLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/leaderboard`,
        { withCredentials: true }
      );
      setLeaderboardData(response.data);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-slate-400" />;
      case 3:
        return <Award className="h-6 w-6 text-amber-600" />;
      default:
        return <span className="font-bold text-slate-500">#{rank}</span>;
    }
  };

  const getRankBgClass = (rank) => {
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200";
      case 2:
        return "bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200";
      case 3:
        return "bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200";
      default:
        return "bg-white border-slate-100";
    }
  };

  const getStreakEmoji = (streak) => {
    if (streak >= 10) return "🔥🔥🔥";
    if (streak >= 5) return "🔥🔥";
    if (streak >= 3) return "🔥";
    return "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!leaderboardData) {
    return (
      <div className="text-center py-12 text-slate-500">
        Unable to load leaderboard data
      </div>
    );
  }

  const { period, individual_rankings, team_rankings, top_performers } = leaderboardData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Trophy className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leaderboard</h1>
            <p className="text-slate-500">{period.month} Performance Rankings</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Calendar className="h-4 w-4" />
          <span>{period.total_workdays} workdays tracked</span>
        </div>
      </div>

      {/* Top 3 Performers Podium */}
      {top_performers.length > 0 && (
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              <CardTitle>Top Performers</CardTitle>
            </div>
            <CardDescription>This month&apos;s leading contributors</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Reorder for podium display: 2nd, 1st, 3rd */}
              {[top_performers[1], top_performers[0], top_performers[2]].map((performer, idx) => {
                if (!performer) return null;
                const isFirst = idx === 1;
                return (
                  <div
                    key={performer.user_id}
                    className={`relative p-4 rounded-xl border-2 ${getRankBgClass(performer.rank)} ${
                      isFirst ? "md:-mt-4 md:mb-4" : ""
                    } transition-all hover:shadow-md`}
                  >
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      {getRankIcon(performer.rank)}
                    </div>
                    <div className="text-center pt-4">
                      <h3 className="font-semibold text-lg text-slate-900">
                        {performer.name}
                      </h3>
                      <p className="text-sm text-slate-500 mb-3">{performer.email}</p>
                      <div className="space-y-2">
                        <div className="flex justify-center">
                          <Badge 
                            variant={isFirst ? "default" : "secondary"} 
                            className={`text-lg px-3 py-1 ${isFirst ? "bg-yellow-500 hover:bg-yellow-600" : ""}`}
                          >
                            {performer.combined_score}%
                          </Badge>
                        </div>
                        <div className="flex justify-center gap-2 text-xs">
                          <span className="flex items-center gap-1">
                            <Target className="h-3 w-3 text-green-500" />
                            {performer.consistency_rate}%
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-blue-500" />
                            {performer.on_time_rate}%
                          </span>
                        </div>
                        {performer.current_streak > 0 && (
                          <div className="flex justify-center items-center gap-1 text-sm">
                            <Flame className="h-4 w-4 text-orange-500" />
                            <span>{performer.current_streak} day streak</span>
                            <span>{getStreakEmoji(performer.current_streak)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Target className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg Consistency</p>
                <p className="text-2xl font-bold text-slate-900">
                  {individual_rankings.length > 0 
                    ? Math.round(individual_rankings.reduce((a, b) => a + b.consistency_rate, 0) / individual_rankings.length)
                    : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg On-Time</p>
                <p className="text-2xl font-bold text-slate-900">
                  {individual_rankings.length > 0 
                    ? Math.round(individual_rankings.reduce((a, b) => a + b.on_time_rate, 0) / individual_rankings.length)
                    : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Flame className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Longest Streak</p>
                <p className="text-2xl font-bold text-slate-900">
                  {Math.max(...individual_rankings.map(r => r.longest_streak), 0)} days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Participants</p>
                <p className="text-2xl font-bold text-slate-900">
                  {individual_rankings.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rankings Tabs */}
      <Tabs defaultValue="individual" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="individual" className="flex items-center gap-2">
            <Star className="h-4 w-4" />
            Individual
          </TabsTrigger>
          <TabsTrigger value="teams" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Teams
          </TabsTrigger>
        </TabsList>

        {/* Individual Rankings */}
        <TabsContent value="individual" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Individual Rankings</CardTitle>
              <CardDescription>
                Performance based on submission consistency (60%) and on-time rate (40%)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Rank</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Name</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">
                        <span className="flex items-center justify-end gap-1">
                          <Target className="h-4 w-4" /> Consistency
                        </span>
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">
                        <span className="flex items-center justify-end gap-1">
                          <Clock className="h-4 w-4" /> On-Time
                        </span>
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Score</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">
                        <span className="flex items-center justify-end gap-1">
                          <Flame className="h-4 w-4" /> Streak
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {individual_rankings.map((user) => (
                      <tr 
                        key={user.user_id} 
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${getRankBgClass(user.rank)}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center w-8">
                            {getRankIcon(user.rank)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{user.name}</p>
                            <p className="text-sm text-slate-500">{user.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex flex-col items-end">
                            <span className={`font-semibold ${
                              user.consistency_rate >= 80 ? "text-green-600" :
                              user.consistency_rate >= 60 ? "text-yellow-600" : "text-red-600"
                            }`}>
                              {user.consistency_rate}%
                            </span>
                            <span className="text-xs text-slate-400">
                              {user.submitted_days}/{user.total_workdays} days
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-semibold ${
                            user.on_time_rate >= 80 ? "text-blue-600" :
                            user.on_time_rate >= 60 ? "text-yellow-600" : "text-orange-600"
                          }`}>
                            {user.on_time_rate}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Badge variant={user.rank <= 3 ? "default" : "secondary"}>
                            {user.combined_score}%
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {user.current_streak > 0 ? (
                              <>
                                <span className="font-medium">{user.current_streak}</span>
                                <span>{getStreakEmoji(user.current_streak)}</span>
                              </>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {individual_rankings.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No data available for this month yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Rankings */}
        <TabsContent value="teams" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Team Rankings</CardTitle>
              <CardDescription>
                Team performance based on average member scores
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {team_rankings.map((team) => (
                  <div
                    key={team.team_id}
                    className={`p-4 rounded-lg border-2 ${getRankBgClass(team.rank)} hover:shadow-md transition-all`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10">
                          {getRankIcon(team.rank)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{team.team_name}</h3>
                          <p className="text-sm text-slate-500">
                            {team.leader_names.length > 0 
                              ? `Led by ${team.leader_names.join(", ")}`
                              : "No leader assigned"
                            }
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-lg px-3 py-1">
                        {team.avg_combined_score}%
                      </Badge>
                    </div>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <p className="text-sm text-slate-500">Members</p>
                        <p className="font-semibold text-slate-900">{team.member_count}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-slate-500">Consistency</p>
                        <p className="font-semibold text-green-600">{team.avg_consistency_rate}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-slate-500">On-Time</p>
                        <p className="font-semibold text-blue-600">{team.avg_on_time_rate}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-slate-500">Top Performer</p>
                        <p className="font-semibold text-purple-600">{team.top_performer || "-"}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {team_rankings.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No team data available for this month yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Legend */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-green-500" />
              <span><strong>Consistency:</strong> % of workdays with log submitted</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <span><strong>On-Time:</strong> Submitted before {period.reminder_time} ET</span>
            </div>
            <div className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-orange-500" />
              <span><strong>Streak:</strong> Consecutive workdays with submissions</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-500" />
              <span><strong>Score:</strong> 60% consistency + 40% on-time</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
